/**
 * #513 (cabo suelto de #384) — EL PREVIEW TIENE QUE DECIR LO QUE VA A PASAR.
 *
 * ---------------------------------------------------------------------------
 * EL PROBLEMA
 * ---------------------------------------------------------------------------
 * Desde #384 la aprobación reparte POR CANTIDAD:
 *     retirar = min(necesario, inventory.current_stock)   /   comprar = resto
 *
 * Pero las dos pantallas donde se aprueba (`MaterialRequestsInbox` y
 * `SupervisorDashboard`) seguían contando con `fulfillment_type`, que lo calcula
 * la máquina con la MISMA regla todo-o-nada que #384 vino a reemplazar
 * (`best.current_stock >= qty ? 'from_stock' : 'needs_purchase'`). Para 100
 * tubos con 60 en bodega el preview decía "0 de bodega, 1 de compra" y la
 * aprobación retiraba 60 y compraba 40. Quien aprueba decide mirando ese
 * número: le estábamos mostrando lo contrario de lo que iba a pasar.
 *
 * Acá NO se recalcula nada: se llama a `planMaterialSplit`, el MISMO módulo que
 * usa la aprobación (`materialRequestService.approveSmartRequest` y su gemelo
 * MCP). Si el reparto cambia, el preview cambia con él por construcción.
 *
 * ---------------------------------------------------------------------------
 * POR QUÉ ESTE ARCHIVO EXISTE (y no dos copias en las dos páginas)
 * ---------------------------------------------------------------------------
 * El bug de #513 ES la duplicación: el conteo estaba copiado línea por línea en
 * las dos pantallas, así que "arreglar el inbox" habría dejado al supervisor
 * mintiendo igual. Misma familia que #353 y #454. Todo lo que las dos páginas
 * comparten (parseo de ítems, clasificación, texto, badges) vive acá y en
 * `components/materials/MaterialSplitPreview.js`; las páginas sólo lo montan.
 *
 * ---------------------------------------------------------------------------
 * DECISIÓN: EL PREVIEW MUESTRA EL PLAN, NO LA DEGRADACIÓN DEL FIFO (#355)
 * ---------------------------------------------------------------------------
 * `inventory_batches` está VACÍA en prod, así que hoy `consume_inventory_fifo`
 * devuelve "Insufficient stock. Available: 0" para todo y la aprobación cae a su
 * fallback: lo que el FIFO no pudo retirar se compra. Si el preview simulara ese
 * fallback mostraría "comprar 100" en cada ítem — exactamente el número
 * equivocado que #513 viene a arreglar, ahora con más código — y volvería a
 * mentir el día que se backfilleen los lotes.
 *
 * Entonces el preview promete la DECISIÓN (que sale de `current_stock`, la única
 * autoridad de cuánto hay, igual que la aprobación) y no el RESULTADO de
 * ejecutarla. Lo ejecutado ya se reporta después de aprobar: `approveSmartRequest`
 * devuelve `notices` con el reparto real y las fallas del FIFO, y las dos
 * pantallas lo muestran. Preview = qué se va a intentar; notices = qué pasó.
 *
 * ---------------------------------------------------------------------------
 * STOCK ILEGIBLE = "NO SÉ", NUNCA UN NÚMERO INVENTADO
 * ---------------------------------------------------------------------------
 * Si la query de `inventory` falla o la fila no vino por RLS, `planMaterialSplit`
 * marca `stock_unknown` y cae al ruteo viejo. Esos ítems NO se cuentan en
 * "bodega"/"compra"/"parcial": van a su propio bucket `unknown`. Un "retirar 0 /
 * comprar 100" cuando en realidad no sabemos es peor que decir que no sabemos.
 *
 * @module utils/materialSplitPreview
 */

import { planMaterialSplit, formatQty } from './materialSplit';

/**
 * Buckets del preview. `unknown` es excluyente: si no sabemos, no contamos.
 *
 * #664 — `no_qty` GANA SOBRE TODOS. Desde #664 una línea puede existir sin
 * cantidad (`quantity_requested` NULL = "nadie la dijo"): la captura no se
 * bloquea porque el técnico está en obra. `planMaterialSplit` traduce ese NULL a
 * `needed = 0` —es su degradación conservadora de siempre— y sin este bucket el
 * preview le mostraría "Comprar 0" a quien está por aprobar. Ese cero se lee
 * como una decisión ("no hay que comprar nada de esto") cuando en realidad es un
 * dato que falta, y quien aprueba decide mirando justamente ese número.
 *
 * No se toca `materialSplit.js` para esto a propósito: ese módulo tiene un
 * gemelo byte-equivalente en `mcp-server/` con un test de paridad que compara
 * los fuentes carácter por carácter. La lectura del hueco es cosa de la VISTA, y
 * la vista es este archivo.
 */
export const SPLIT_KIND = {
  STOCK: 'stock',
  PARTIAL: 'partial',
  PURCHASE: 'purchase',
  UNKNOWN: 'unknown',
  NO_QTY: 'no_qty',
};

/** #664 — "nadie dijo cuántas". NULL/'' /NaN, nunca un 0 real (que el CHECK impide). */
export const sinCantidad = (item) => {
  const q = item?.quantity_requested;
  if (q == null || q === '') return true;
  return !Number.isFinite(parseFloat(q));
};

export const MATCH_LABELS = {
  matched: 'Inventario',
  fuzzy: 'Posible match',
  unmatched: 'Nuevo ítem',
  user_confirmed_new: 'Nuevo ítem',
};

/**
 * Los ítems llegan como array (Supabase) o como string JSON según la vista y el
 * cliente. Las dos páginas lo destrababan distinto (una parseaba, la otra
 * devolvía `[]` y perdía la lista); acá hay una sola forma.
 */
export const parseRequestItems = (req) => {
  const raw = req?.items;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

/** Todos los `inventory_id` distintos de una tanda de solicitudes (una sola query). */
export const collectInventoryIds = (requests = []) => {
  const ids = new Set();
  for (const req of requests || []) {
    for (const item of parseRequestItems(req)) {
      if (item?.inventory_id) ids.add(item.inventory_id);
    }
  }
  return Array.from(ids).sort();
};

/** A qué bucket va un plan. `no_qty` gana sobre todo, después `stock_unknown`. */
export const classifyPlan = (plan) => {
  if (!plan) return SPLIT_KIND.PURCHASE;
  // #664 primero: sin cantidad no hay reparto que mostrar, ni siquiera "sin
  // confirmar". No es que no sepamos cuánto hay en bodega — es que no sabemos
  // cuánto hace falta.
  if (sinCantidad(plan.item)) return SPLIT_KIND.NO_QTY;
  if (plan.stock_unknown) return SPLIT_KIND.UNKNOWN;
  if (plan.quantity_from_stock > 0 && plan.quantity_to_purchase > 0) return SPLIT_KIND.PARTIAL;
  if (plan.quantity_from_stock > 0) return SPLIT_KIND.STOCK;
  return SPLIT_KIND.PURCHASE;
};

/** "unid" sí, "(sin especificar)" no. */
const unitOf = (item) => {
  const u = typeof item?.unit === 'string' ? item.unit.trim() : '';
  return u && u !== '(sin especificar)' ? ` ${u}` : '';
};

/**
 * El texto que reemplaza al badge binario. Es la única frase que ve quien
 * aprueba, así que dice cantidades, no categorías.
 */
export const describePlan = (plan) => {
  if (!plan) return '';
  const u = unitOf(plan.item);
  switch (classifyPlan(plan)) {
    // #664 — se dice lo que falta y qué hacer, no un número. "Comprar 0" sería
    // un número inventado con cara de decisión.
    case SPLIT_KIND.NO_QTY:
      return 'Sin cantidad — el líder no la dijo. Completala antes de aprobar.';
    case SPLIT_KIND.UNKNOWN:
      return `${formatQty(plan.needed)}${u} — reparto sin confirmar`;
    case SPLIT_KIND.PARTIAL:
      return `Retirar ${formatQty(plan.quantity_from_stock)} / comprar ${formatQty(plan.quantity_to_purchase)}${u}`;
    case SPLIT_KIND.STOCK:
      return `Retirar ${formatQty(plan.quantity_from_stock)}${u} de bodega`;
    default:
      return `Comprar ${formatQty(plan.quantity_to_purchase)}${u}`;
  }
};

/**
 * Solicitud de UN ítem: cabecera sola, sin filas en `material_request_items`.
 * Es lo que escribe `create_material_request` (web y MCP).
 *
 * Medido en prod el 2026-09-10 (MR-2026-0022): el agente creó una externa bien
 * formada (`is_external`, `external_description: "Cemento mixto"`, 2 sacos) y
 * la bandeja admin la mostraba como "1 ítem · sin reparto" con el detalle
 * VACÍO — sólo sabía dibujar el caso con `inventory_id`, y ahí mostraba el
 * UUID. `SupervisorDashboard` tenía su propia versión que sí leía la
 * descripción externa: la misma duplicación de #513, en el camino de un ítem.
 *
 * Tres casos, y los tres dicen la CONSECUENCIA de aprobar, porque es lo que
 * decide quien mira la cola:
 *   - EXTERNAL   → se compra aparte: aprobar no consume stock ni genera la PO
 *                  (eso sólo lo hace el camino smart).
 *   - INVENTORY  → sale de bodega al aprobar.
 *   - MISSING    → no identifica material (ni `inventory_id` ni descripción).
 *                  Hay tres así del 13/08 en prod, con el "material" sólo en
 *                  `unit` ("varillas"). No se puede mostrar lo que no está; sí
 *                  se puede DECIR que no está, en vez de un chip genérico.
 */
export const SIMPLE_KIND = {
  INVENTORY: 'inventory',
  EXTERNAL: 'external',
  MISSING: 'missing',
};

export const describeSimpleRequest = (req = {}) => {
  // #664 — sin cantidad no se inventa un número.
  const cantidad = sinCantidad(req)
    ? 'sin cantidad'
    : `${formatQty(parseFloat(req.quantity_requested))}${unitOf(req)}`;
  const supplier = typeof req.external_supplier === 'string' ? req.external_supplier.trim() : '';

  if (req.is_external) {
    const name = typeof req.external_description === 'string' ? req.external_description.trim() : '';
    return {
      kind: SIMPLE_KIND.EXTERNAL,
      name: name || null,
      cantidad,
      label: `${cantidad} · ${name || 'material externo sin descripción'}`,
      title: `Material externo: ${name || '(sin descripción)'}`,
      consequence: 'Se compra aparte: aprobar no consume stock ni genera orden de compra.',
      supplier: supplier || null,
      searchText: [name, supplier].filter(Boolean).join(' '),
    };
  }

  if (req.inventory_id) {
    // El nombre viene del embed `inventory_item:inventory(...)`. Si la página
    // no lo pidió, se dice que falta — nunca el UUID con cara de nombre.
    const name = req.inventory_item?.name ? String(req.inventory_item.name).trim() : '';
    return {
      kind: SIMPLE_KIND.INVENTORY,
      name: name || null,
      cantidad,
      label: `${cantidad} · ${name || 'ítem de inventario'}`,
      title: name || 'Ítem de inventario (nombre no cargado)',
      consequence: 'Sale de bodega al aprobar — reparto al aprobar.',
      supplier: null,
      searchText: name,
    };
  }

  return {
    kind: SIMPLE_KIND.MISSING,
    name: null,
    cantidad,
    label: `${cantidad} · material sin identificar`,
    title: 'Material sin identificar',
    consequence: 'La solicitud no identifica ningún material (ni ítem del catálogo ni descripción externa): aprobarla no consume stock ni genera compra. Rechazala o pedí que la carguen de nuevo.',
    supplier: null,
    searchText: '',
  };
};

/**
 * El preview de una solicitud: el plan por ítem + el conteo de abajo.
 *
 * @param {Array<object>} items - `material_request_items` de la solicitud
 * @param {Map<string, number>|null} stockById - `current_stock` por inventory_id.
 *        `null` (query fallida, o todavía cargando) = TODO desconocido.
 * @returns {{plans: Array<object>, counts: object, total: number, hasUnknown: boolean}}
 */
export const summarizeSplitPreview = (items = [], stockById = null) => {
  const plans = planMaterialSplit(items, stockById).map((plan) => ({
    ...plan,
    kind: classifyPlan(plan),
  }));

  const counts = { stock: 0, partial: 0, purchase: 0, unknown: 0, no_qty: 0 };
  for (const plan of plans) counts[plan.kind] += 1;

  return {
    plans,
    counts,
    total: plans.length,
    hasUnknown: counts.unknown > 0,
    // #664 — la solicitud NO se puede aprobar mientras haya líneas sin cantidad
    // (los dos carriles de aprobación las rebotan). La pantalla lo tiene que
    // saber para no ofrecer un botón que va a fallar.
    hasMissingQty: counts.no_qty > 0,
  };
};

const materialSplitPreview = {
  SPLIT_KIND,
  MATCH_LABELS,
  parseRequestItems,
  collectInventoryIds,
  classifyPlan,
  describePlan,
  sinCantidad,
  summarizeSplitPreview,
  SIMPLE_KIND,
  describeSimpleRequest,
};

export default materialSplitPreview;
